import { useCallback, useMemo, useRef, useState } from 'react'
import { View, Text, TouchableOpacity, ScrollView, Image, StyleSheet, Platform, Linking } from 'react-native'
import { useRouter, useFocusEffect } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { colors, fonts, radius } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { ErrorState } from '../../components/StateViews'
import { useAuth } from '../../lib/AuthContext'
import { markNewsSeen } from '../../lib/newsUnread'

const CATEGORY_COLORS = {
  'Risultati':     '#10B981',
  'Trasferimenti': '#3B82F6',
  'Generale':      '#8B5CF6',
  'Preview':       '#EF4444',
  'Live':          '#ef44d2',
  'Altro':         '#F59E0B',
}

// Notizie esterne (aggregatore): si apre l'articolo originale nel browser, non c'e' una pagina di dettaglio nostra.
const openExternal = (url) => {
  if (!/^https?:\/\//i.test(url ?? '')) return
  if (Platform.OS === 'web') window.open(url, '_blank', 'noopener,noreferrer')
  else Linking.openURL(url).catch(() => {})
}

export default function News() {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const router = useRouter()
  const styles = useMemo(() => makeStyles(tc), [tc])

  const insets = useSafeAreaInsets()
  const { user } = useAuth()
  const [news, setNews]       = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  // silent: ricarica senza mostrare "Caricamento" (quando la lista c'e' gia)
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setLoadError(false)
    const { data, error } = await supabase
      .from('dim_news')
      .select('id, title, excerpt, category, published_at, image_url, is_pinned, is_external, source_name, source_url')
      .eq('is_published', true)
      .order('is_pinned', { ascending: false })
      .order('published_at', { ascending: false })
      .limit(80)
    if (error) {
      console.error('[News] errore:', error)
      if (!silent) setLoadError(true)
    } else {
      setNews(data ?? [])
      // l'utente ha visto le news: azzera il numerino sulla scheda (la piu recente fra quelle scritte da noi, come il conteggio)
      const newest = (data ?? []).filter(x => !x.is_external).reduce((m, x) => (x.published_at > m ? x.published_at : m), '')
      markNewsSeen(user?.id, newest || undefined)
    }
    if (!silent) setLoading(false)
  }, [user?.id])

  // Ad ogni apertura della scheda ricarico: senza questo una news pubblicata mentre l'app e aperta
  // comparirebbe nel numerino ma non nella lista finche non si riavvia l'app.
  const firstFocus = useRef(true)
  useFocusEffect(useCallback(() => {
    load(!firstFocus.current)
    firstFocus.current = false
  }, [load]))

  return (
    <ScrollView style={[styles.container, { backgroundColor: tc.bg }]} contentContainerStyle={{ paddingBottom: insets.bottom + 32 }}>

      {/* Section label */}
      <View style={styles.sectionLabel}>
        <View style={styles.sectionBar} />
        <Text style={[styles.sectionText, { color: tc.text }]}>{t('news.title')}</Text>
      </View>

      {loading && (
        <Text style={[styles.center, { color: tc.textMuted }]}>{t('news.loading')}</Text>
      )}
      {!loading && loadError && (
        <ErrorState message={t('news.load_error')} onRetry={() => load()} />
      )}
      {!loading && !loadError && news.length === 0 && (
        <Text style={[styles.center, { color: tc.textMuted }]}>{t('news.no_news')}</Text>
      )}

      <View style={{ gap: 12 }}>
        {news.map(n => (
          <TouchableOpacity
            key={n.id}
            style={[styles.card, { backgroundColor: tc.bgCard, borderColor: n.is_pinned ? tc.rosa : tc.border }]}
            onPress={() => (n.is_external ? openExternal(n.source_url) : router.push(`/News/${n.id}`))}
            accessibilityRole={n.is_external ? 'link' : 'button'}
            accessibilityLabel={n.is_external ? t('news.open_external', { source: n.source_name }) : undefined}
            activeOpacity={0.85}
          >
            {/* Immagine copertina */}
            {!n.is_external && n.image_url && (
              <Image
                source={{ uri: n.image_url }}
                style={styles.cardImage}
                resizeMode="cover"
              />
            )}

            <View style={styles.cardBody}>
              {/* Badge + categoria + data */}
              <View style={styles.metaRow}>
                {n.is_pinned && (
                  <View style={styles.pinnedBadge}>
                    <Text style={styles.pinnedBadgeText}>{t('news.pinned')}</Text>
                  </View>
                )}
                <View style={[styles.catBadge, { backgroundColor: n.is_external ? tc.border : (CATEGORY_COLORS[n.category] ?? '#8B5CF6') }]}>
                  <Text style={[styles.catBadgeText, n.is_external && { color: tc.text }]}>{n.is_external ? `${n.source_name} ↗` : n.category}</Text>
                </View>
                <Text style={[styles.dateText, { color: tc.textMuted }]}>
                  {n.published_at ? new Date(n.published_at).toLocaleDateString('it-IT', {
                    day: '2-digit', month: 'long', year: 'numeric'
                  }) : ''}
                </Text>
              </View>

              {/* Titolo */}
              <Text style={[styles.cardTitle, { color: tc.text }]}>{n.title}</Text>

              {/* Excerpt */}
              {!n.is_external && n.excerpt && (
                <Text style={[styles.cardExcerpt, { color: tc.textMuted }]} numberOfLines={2}>
                  {n.excerpt}
                </Text>
              )}
            </View>
          </TouchableOpacity>
        ))}
      </View>

      {news.some(n => n.is_external) && (
        <Text style={[styles.disclaimer, { color: tc.textMuted }]}>{t('news.disclaimer')}</Text>
      )}
    </ScrollView>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  container:    { flex: 1, backgroundColor: tc.bg },
  sectionLabel: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 16, paddingBottom: 20 },
  sectionBar:   { width: 3, height: 18, backgroundColor: tc.rosa, borderRadius: 2 },
  sectionText:  { fontFamily: fonts.condensedBold, fontSize: 17, textTransform: 'uppercase', letterSpacing: 0.5 },
  center:       { textAlign: 'center', padding: 40, fontSize: 13 },
  disclaimer:   { textAlign: 'center', fontSize: 11, lineHeight: 16, paddingHorizontal: 24, marginTop: 18 },

  card:         { borderRadius: 14, borderWidth: 1, overflow: 'hidden', marginHorizontal: 16 },
  cardImage:    { width: '100%', height: 160 },
  cardBody:     { padding: 14, paddingHorizontal: 16 },

  metaRow:      { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' },
  pinnedBadge:  { backgroundColor: tc.rosaBorder, borderRadius: 20, paddingHorizontal: 7, paddingVertical: 2 },
  pinnedBadgeText: { fontSize: 10, color: tc.rosaText, fontFamily: fonts.condensedBold, letterSpacing: 0.5, textTransform: 'uppercase' },
  catBadge:     { borderRadius: 20, paddingHorizontal: 8, paddingVertical: 3 },
  catBadgeText: { color: tc.brandText, fontSize: 10, fontFamily: fonts.condensedBold, textTransform: 'uppercase', letterSpacing: 0.5 },
  dateText:     { fontSize: 11 },

  cardTitle:    { fontSize: 16, fontWeight: '700', marginBottom: 6 },
  cardExcerpt:  { fontSize: 13, lineHeight: 20 },
})
